import { OffsetEstimator, syncTick, type SyncTick } from "@riffbox/clock";
import type { Clock, ServerToClient, ClientToServer } from "@riffbox/protocol";

const DRIFT_LOOP_MS = 250;
const PROBE_BURST = 5;
const PROBE_BURST_GAP_MS = 200;
const PROBE_PERIOD_MS = 10_000;
const STATUS_PERIOD_MS = 500; // continuous playback telemetry (drives the gate)
const READY_BUFFER_SEC = 2; // buffered-ahead needed to count as "ready"

/**
 * Drives a media element to the room's authoritative clock (PRD §5). Owns the
 * NTP-lite offset estimate, the periodic time-sync probes, and the 250 ms drift
 * loop that applies `syncTick` outcomes to the element. The decision logic lives
 * in `@riffbox/clock` (and is proven by the convergence simulator); this class
 * is just the browser plumbing around it.
 */
export class SyncEngine {
  private clock: Clock | null = null;
  private readonly offset = new OffsetEstimator();
  private loop: ReturnType<typeof setInterval> | null = null;
  private probeTimer: ReturnType<typeof setInterval> | null = null;
  private statusTimer: ReturnType<typeof setInterval> | null = null;
  private lastSeekMs = -Infinity;
  private generation = 0;
  /** Exposed for the UI/e2e: last applied target & delta. */
  lastTick: SyncTick | null = null;
  /** Fired when the room is playing but the browser blocked our programmatic
   *  play() (autoplay policy) — the UI shows a one-tap "play" affordance. */
  onBlocked?: (blocked: boolean) => void;

  constructor(
    private readonly video: HTMLMediaElement,
    private readonly send: (msg: ClientToServer) => void,
    private readonly now: () => number = () => Date.now(),
  ) {}

  start(): void {
    if (this.loop) return;
    this.probeBurst();
    this.probeTimer = setInterval(() => this.probeBurst(), PROBE_PERIOD_MS);
    this.loop = setInterval(() => this.tick(), DRIFT_LOOP_MS);
    this.statusTimer = setInterval(() => this.sendStatus(), STATUS_PERIOD_MS);
  }

  stop(): void {
    if (this.loop) clearInterval(this.loop);
    if (this.probeTimer) clearInterval(this.probeTimer);
    if (this.statusTimer) clearInterval(this.statusTimer);
    this.loop = null;
    this.probeTimer = null;
    this.statusTimer = null;
  }

  /** After a reconnect the server (and our offset) may have moved; re-probe. */
  onReconnect(): void {
    this.offset.reset();
    this.lastSeekMs = -Infinity; // allow an immediate re-position after reconnect
    this.probeBurst();
  }

  hasOffset(): boolean {
    return this.offset.hasEstimate;
  }

  estimatedOffsetMs(): number {
    return this.offset.offsetMs;
  }

  onMessage(msg: ServerToClient): void {
    switch (msg.type) {
      case "room_state":
        this.applyClock(msg.clock);
        break;
      case "clock":
        this.applyClock({
          state: msg.state,
          positionSec: msg.positionSec,
          anchorServerMs: msg.anchorServerMs,
          generation: msg.generation,
        });
        break;
      case "time_sync_reply": {
        const t3 = this.now();
        this.offset.add({ t0: msg.t0, serverRecvMs: msg.serverRecvMs, serverSendMs: msg.serverSendMs, t3 });
        break;
      }
      default:
        break;
    }
  }

  /**
   * Apply an authoritative clock update from a host control (play/pause/seek) or
   * join. This is a deliberate jump, so we reset the seek cooldown — the guest
   * follows the host's seek IMMEDIATELY instead of being throttled by the
   * drift-correction cooldown (which exists only to tame network-induced drift).
   */
  private applyClock(clock: Clock): void {
    this.clock = clock;
    this.generation = clock.generation;
    this.lastSeekMs = -Infinity;
    this.tick();
    // Report readiness for the new generation immediately so the gate doesn't
    // wait a full status interval for us.
    this.sendStatus();
  }

  /** Seconds of contiguous media buffered ahead of the current position. */
  private bufferedAhead(): number {
    const t = this.video.currentTime;
    const b = this.video.buffered;
    for (let i = 0; i < b.length; i++) {
      if (t >= b.start(i) - 0.25 && t <= b.end(i)) return Math.max(0, b.end(i) - t);
    }
    return 0;
  }

  private sendStatus(): void {
    const ahead = this.bufferedAhead();
    this.send({
      type: "status",
      positionSec: this.video.currentTime,
      bufferedAheadSec: ahead,
      ready: this.video.readyState >= 3 /* HAVE_FUTURE_DATA */ && ahead >= READY_BUFFER_SEC,
      generation: this.generation,
    });
  }

  private probeBurst(): void {
    for (let i = 0; i < PROBE_BURST; i++) {
      setTimeout(() => this.send({ type: "time_sync", t0: this.now() }), i * PROBE_BURST_GAP_MS);
    }
  }

  private tick(): void {
    if (!this.clock || !this.offset.hasEstimate) return;
    const estServerNow = this.offset.estimatedServerNow(this.now());
    const tick = syncTick(this.clock, estServerNow, {
      currentSec: this.video.currentTime,
      // readyState < HAVE_FUTURE_DATA (3) ⇒ can't play forward ⇒ buffering.
      buffering: this.video.readyState < 3,
      msSinceSeek: this.now() - this.lastSeekMs,
    });
    this.lastTick = tick;

    // Apply position/rate FIRST, so a seek can't abort a concurrent play().
    switch (tick.outcome.kind) {
      case "seek":
        this.video.currentTime = tick.outcome.seekToSec;
        this.video.playbackRate = 1;
        this.lastSeekMs = this.now();
        break;
      case "slew":
        this.video.playbackRate = tick.outcome.playbackRate;
        break;
      case "nominal":
      case "wait":
        // "wait" = buffering; keep nominal rate and let the network recover.
        this.video.playbackRate = 1;
        break;
    }

    // Then reconcile playing/paused with the room.
    if (tick.paused) {
      if (!this.video.paused) this.video.pause();
      this.onBlocked?.(false);
    } else if (this.video.paused) {
      // The room is playing but our element isn't — try to start it. If the
      // browser blocks the programmatic play (autoplay policy), surface a tap.
      this.video.play().then(
        () => this.onBlocked?.(false),
        () => this.onBlocked?.(true),
      );
    } else {
      this.onBlocked?.(false);
    }
  }
}
